/**
 * 69.4 step 6's bot half (b), the commander's HMG errand (the coordinator's brief; DECISIONS 166 (4), 171 Q6): with build
 * kits on, the bot commander emplaces an HMG at a point it holds — the side's own among the points its squads are told to
 * take or hold — when none of the side's HMGs stands within HMG_HOLD.spacing people of it: at the first spot on rings
 * HMG_HOLD.ring people out from the point toward the side's home that the build path takes (`buildRefusal`: the kit, the
 * cost, the ground, the slope and the clearance in one question), by the nearest free bot on foot (a bot nobody holds, no
 * task, no human's order). One HMG at a time (none while one's build task is in force); the planting is the build path's, the bot's walk the build brain's.
 * Pure: it returns the task to issue; the commander issues it (ai/squadOrders.ts, one line beside `planBuilds`).
 */
import type { GameState, PlayerState, SquadOrder, Vec3 } from '../types';
import { HMG_HOLD } from '../constants';
import { buildOn, buildsLeft, buildRefusal } from './build';
import { structureTeam } from './body';
import { squadTaskFor, squadOrderSourceFor } from './squads';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub } from '../math';
import { dcos, dsin } from './dmath';

export type HmgTask = { unitId: number; order: Extract<SquadOrder, { kind: 'build' }> };

/** Where `team`'s home lies (its HQ, else its board base, else its landing), or null. */
function homeDir(state: GameState, team: number): Vec3 | null {
  const hq = state.structures.filter(s => s.type === 'ecotourism' && s.health > 0 && structureTeam(state, s) === team).sort((a, b) => a.id - b.id)[0];
  return hq?.surfaceDir ?? state.board?.bases.find(b => b.team === team)?.dir ?? state.actionHomes?.[team] ?? null;
}

/**
 * The HMG to emplace this re-plan for `team`, or null: `pointIds` are the commander's targets (take / hold) this re-plan;
 * the first of them (by id) the side holds with no HMG of its own near gets one, if a spot and a planter are found.
 */
export function planHmg(state: GameState, team: number, pointIds: readonly number[], tick: number): HmgTask | null {
  if (!buildOn(state.config, 'hmg')) return null;
  const free = (p: PlayerState) => p.alive && p.teamIndex === team && p.isBot && state.controllers?.[p.id] === undefined && !p.mount && !p.airborne
    && squadTaskFor(state, p.id, tick) === null && squadOrderSourceFor(state, p.id, tick) !== 'human';
  const lead = state.players.find(p => p.teamIndex === team);
  if (!lead || buildsLeft(state, lead, 'hmg') <= 0) return null;
  // one under way at a time: none while a unit of the side holds an HMG's build task (it is walking there or planting)
  if (state.players.some(p => p.alive && p.teamIndex === team && (() => { const t = squadTaskFor(state, p.id, tick); return !!t && t.order.kind === 'build' && t.order.structure === 'hmg'; })())) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const home = homeDir(state, team);
  const sites = [...new Set(pointIds)].sort((a, b) => a - b).map(id => state.modeState.points?.sites.find(s => s.id === id)).filter(s => !!s && s.owner === team);
  for (const site of sites) {
    const planet = state.planets[site!.planetIndex];
    if (!planet) continue;
    const near = state.structures.some(s => s.type === 'hmg' && s.health > 0 && s.planetIndex === site!.planetIndex && structureTeam(state, s) === team
      && arcDistance(s.surfaceDir, site!.surfaceDir, planet.radius) <= HMG_HOLD.spacing * person);
    if (near) continue;
    // toward home on the sphere: the tangent at the point toward the home's direction (any tangent with no home)
    const c = vecNormalize(site!.surfaceDir);
    const h = home ? vecNormalize(home) : null;
    const t0 = h ? vecSub(h, vecScale(c, vecDot(h, c))) : null;
    const tl = t0 ? Math.sqrt(vecDot(t0, t0)) : 0;
    const t = tl > 1e-9 ? vecScale(t0!, 1 / tl) : vecNormalize(Math.abs(c.y) < 0.9 ? vecSub({ x: 0, y: 1, z: 0 }, vecScale(c, c.y)) : vecSub({ x: 1, y: 0, z: 0 }, vecScale(c, c.x)));
    for (const people of HMG_HOLD.ring) {
      const th = (people * person) / planet.radius;
      const dir = vecNormalize(vecAdd(vecScale(c, dcos(th)), vecScale(t, dsin(th))));
      const spot = { planetIndex: site!.planetIndex, surfaceDir: dir };
      if (buildRefusal(state, 'hmg', spot, team) !== null) continue;
      let who: PlayerState | null = null, best = Infinity;
      for (const p of state.players) {
        if (p.planetIndex !== spot.planetIndex || !free(p) || buildsLeft(state, p, 'hmg') <= 0) continue;
        const d = arcDistance(p.surfaceDir, dir, planet.radius);
        if (d < best) { who = p; best = d; }
      }
      if (!who) return null;   // nobody free to send: none this re-plan
      return { unitId: who.id, order: { kind: 'build', structure: 'hmg', planetIndex: spot.planetIndex, surfaceDir: dir } };
    }
  }
  return null;
}
